import {Button, HStack, Text} from '@chakra-ui/react';
import {DELETE_FORBIDDEN_MESSAGE} from '../domain/permissions';

interface SelectionActionBarProps {
    count: number;
    onDownload: () => void;
    isDownloading?: boolean;
    onRename?: () => void;
    /** Set for a single selected picture in an album's web bin: makes it the album cover. */
    onSetCover?: () => void;
    /** Why the cover can't be set right now (shown on hover); the button is disabled while set. */
    setCoverDisabledReason?: string;
    isSettingCover?: boolean;
    showMoveToArchive?: boolean;
    onMoveToArchive?: () => void;
    isMovingToArchive?: boolean;
    onMove: () => void;
    /** Omitted when the selection can't be copied (it has folders). */
    onCopy?: () => void;
    onDelete: () => void;
    /** False when the user may not delete (every item of) the selection. */
    canDelete?: boolean;
    onClear: () => void;
}

export function SelectionActionBar({
    count,
    onDownload,
    isDownloading,
    onRename,
    onSetCover,
    setCoverDisabledReason,
    isSettingCover,
    showMoveToArchive,
    onMoveToArchive,
    isMovingToArchive,
    onMove,
    onCopy,
    onDelete,
    canDelete = true,
    onClear,
}: SelectionActionBarProps) {
    return <HStack
        gap={2}
        bg="gray.50"
        borderRadius="md"
        p={2}
        flexWrap="wrap"
        // Hidden on phones in landscape, where it would take much of the height: the long-press menu
        // offers the same actions there.
        css={{'@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)': {display: 'none'}}}
    >
        <Text fontSize="sm" fontWeight="semibold" px={2}>{count} kijelölve</Text>
        <Button size="sm" variant="subtle" onClick={onDownload} loading={isDownloading}>Letöltés</Button>
        {onRename &&
            <Button size="sm" variant="subtle" onClick={onRename} disabled={count !== 1}>Átnevezés</Button>
        }
        {onSetCover &&
            <Button
                size="sm"
                variant="subtle"
                onClick={onSetCover}
                disabled={!!setCoverDisabledReason}
                loading={isSettingCover}
                title={setCoverDisabledReason}
            >
                Legyen borítókép
            </Button>
        }
        {showMoveToArchive && onMoveToArchive &&
            <Button size="sm" variant="subtle" onClick={onMoveToArchive} loading={isMovingToArchive}>Áthelyezés az archívumba</Button>
        }
        <Button size="sm" variant="subtle" onClick={onMove}>Áthelyezés…</Button>
        {onCopy &&
            <Button size="sm" variant="subtle" onClick={onCopy}>Másolás…</Button>
        }
        <Button
            size="sm"
            colorPalette="red"
            variant="outline"
            // v2's coloured outline: text and border in the 600 shade.
            color="red.600"
            borderColor="currentColor"
            _hover={{bg: 'red.50'}}
            onClick={onDelete}
            disabled={!canDelete}
            title={canDelete ? "Törlés (Del)" : DELETE_FORBIDDEN_MESSAGE}
        >
            Törlés
        </Button>
        <Button size="sm" variant="ghost" onClick={onClear}>Kijelölés törlése</Button>
    </HStack>;
}
